import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import InteractiveMaterial from '../components/InteractiveMaterial';
import { createEmptyMaterialProgress, materialProgressHasAnswers } from '../utils/materialUtils';
import { formatUTCDateTime } from '../utils/scheduleUtils';
import './StudentMaterials.css';

const formatSubmissionDate = (iso) =>
  formatUTCDateTime(new Date(iso), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const MaterialSubmissionReview = ({ submissionId }) => {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubmission = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/lessons/material-submissions/${submissionId}/`);
        setSubmission(response.data);
      } catch (err) {
        console.error('Ошибка загрузки материала:', err);
        setError('Не удалось загрузить материал');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [submissionId]);

  if (loading) {
    return <div className="student-materials-loading">Загрузка...</div>;
  }

  if (error || !submission) {
    return (
      <div className="student-materials-error">
        <p>{error || 'Материал не найден'}</p>
        <button type="button" className="btn-back" onClick={() => navigate('/dashboard/materials')}>
          ← К списку материалов
        </button>
      </div>
    );
  }

  const progress = submission.progress || createEmptyMaterialProgress();
  const content = submission.material_content || [];

  return (
    <div className="student-material-review">
      <button type="button" className="btn-back" onClick={() => navigate('/dashboard/materials')}>
        ← К списку материалов
      </button>
      <div className="student-material-review-meta">
        <span className="student-material-review-teacher">{submission.teacher_name}</span>
        <span className="student-material-review-date">
          Обновлено: {formatSubmissionDate(submission.updated_at)}
        </span>
      </div>
      <div className="student-material-review-body">
        <InteractiveMaterial
          materialId={submission.material}
          title={submission.material_title}
          content={content}
          progress={progress}
          readOnly
          silent
        />
      </div>
    </div>
  );
};

const MaterialsList = ({ compact = false, limit = null }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const response = await api.get('/lessons/material-submissions/');
        const withAnswers = (response.data || []).filter((item) =>
          materialProgressHasAnswers(item.progress)
        );
        setSubmissions(withAnswers);
      } catch (err) {
        console.error('Ошибка загрузки материалов:', err);
        setSubmissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  if (loading) {
    return <div className="student-materials-loading">Загрузка...</div>;
  }

  const visibleSubmissions = limit ? submissions.slice(0, limit) : submissions;

  if (visibleSubmissions.length === 0) {
    return (
      <div className="materials-empty">
        <span className="materials-icon">📚</span>
        <p>Материалы появятся после начала обучения</p>
      </div>
    );
  }

  return (
    <div className={`student-materials-list ${compact ? 'compact' : ''}`}>
      <ul className="student-materials-items">
        {visibleSubmissions.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="student-materials-item"
              onClick={() => navigate(`/dashboard/materials/${item.id}`)}
            >
              <span className="student-materials-item-title">{item.material_title}</span>
              <span className="student-materials-item-meta">
                {item.teacher_name} · {formatSubmissionDate(item.updated_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {compact && submissions.length > (limit || 0) && (
        <Link to="/dashboard/materials" className="completed-lessons-link">
          Все материалы →
        </Link>
      )}
      {!compact && submissions.length > 0 && (
        <Link to="/dashboard/home" className="completed-lessons-link">
          ← На главную
        </Link>
      )}
    </div>
  );
};

const StudentMaterials = ({ compact = false, limit = null }) => {
  const { submissionId } = useParams();

  if (submissionId) {
    return (
      <div className="student-materials-page">
        <MaterialSubmissionReview submissionId={submissionId} />
      </div>
    );
  }

  return (
    <div className="student-materials-page">
      {!compact && <h2 className="student-materials-heading">Учебные материалы</h2>}
      <MaterialsList compact={compact} limit={limit} />
    </div>
  );
};

export const StudentMaterialsWidget = () => (
  <StudentMaterials compact limit={3} />
);

export default StudentMaterials;
